import { COLORS, WATERMARK_OPACITY, watermarkSize } from './constants';
import type { CandleRenderOptions } from './types';

/**
 * Draw OHLC candlesticks, current price line, and watermark on a Canvas2D context.
 * Pure function -- no state, no side effects beyond drawing.
 */
export function drawCandles(options: CandleRenderOptions): void {
  const { canvas, data, scales, margins, currentPrice, showCurrentPriceLine, watermarkImage, crosshairPrice } = options;
  const ctx = canvas.getContext('2d');
  if (!ctx || data.length === 0) return;

  const dpr = window.devicePixelRatio || 1;
  const width = canvas.width / dpr;
  const height = canvas.height / dpr;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);

  const chartWidth = width - margins.left - margins.right;
  const chartHeight = height - margins.top - margins.bottom;

  if (chartWidth < 20 || chartHeight < 20) return;

  const { xScale, yScale } = scales;

  // -- Watermark (behind everything) --
  if (watermarkImage) {
    ctx.save();
    ctx.globalAlpha = WATERMARK_OPACITY;
    const wmSize = watermarkSize(chartWidth, chartHeight);
    const wmX = margins.left + (chartWidth - wmSize) / 2;
    const wmY = margins.top + (chartHeight - wmSize) / 2;
    ctx.drawImage(watermarkImage, wmX, wmY, wmSize, wmSize);
    ctx.restore();
  }

  // -- Grid lines (horizontal) --
  ctx.save();
  ctx.strokeStyle = COLORS.gridLine;
  ctx.lineWidth = 1 / dpr;
  const yTicks = yScale.ticks(chartHeight > 300 ? 8 : 6);
  for (const tick of yTicks) {
    const y = yScale(tick);
    if (y >= margins.top && y <= height - margins.bottom) {
      ctx.beginPath();
      ctx.moveTo(margins.left, y);
      ctx.lineTo(margins.left + chartWidth, y);
      ctx.stroke();
    }
  }
  ctx.restore();

  // -- Candlesticks --
  // Calculate candle width based on data density
  const candleSpacing = chartWidth / data.length;
  const candleWidth = Math.max(1, Math.min(candleSpacing * 0.7, 12));
  const wickWidth = Math.max(1 / dpr, candleWidth > 4 ? 1.5 : 1 / dpr);

  for (const bar of data) {
    const date = new Date(bar.timestamp);
    const x = xScale(date);

    // Skip if outside visible area
    if (x < margins.left - candleWidth || x > margins.left + chartWidth + candleWidth) continue;

    const isUp = bar.close >= bar.open;
    const color = isUp ? COLORS.candleUp : COLORS.candleDown;

    const yOpen = yScale(bar.open);
    const yClose = yScale(bar.close);
    const yHigh = yScale(bar.high);
    const yLow = yScale(bar.low);

    // Wick
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = wickWidth;
    ctx.moveTo(x, yHigh);
    ctx.lineTo(x, yLow);
    ctx.stroke();

    // Body
    const bodyTop = Math.min(yOpen, yClose);
    const bodyHeight = Math.max(1 / dpr, Math.abs(yClose - yOpen));
    ctx.fillStyle = color;
    ctx.fillRect(x - candleWidth / 2, bodyTop, candleWidth, bodyHeight);
  }

  // -- Current price dashed line --
  if (showCurrentPriceLine && currentPrice !== null) {
    const priceY = yScale(currentPrice);
    if (priceY >= margins.top && priceY <= height - margins.bottom) {
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = COLORS.currentPrice;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.moveTo(margins.left, priceY);
      ctx.lineTo(margins.left + chartWidth, priceY);
      ctx.stroke();
      ctx.restore();
    }
  }

  // -- Crosshair from GEX hover --
  if (crosshairPrice != null) {
    const crossY = yScale(crosshairPrice);
    if (crossY >= margins.top && crossY <= height - margins.bottom) {
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = COLORS.currentPrice;
      ctx.lineWidth = 0.8;
      ctx.globalAlpha = 0.6;
      ctx.setLineDash([3, 3]);
      ctx.moveTo(margins.left, crossY);
      ctx.lineTo(margins.left + chartWidth, crossY);
      ctx.stroke();
      ctx.restore();
    }
  }

  // -- Time axis labels --
  ctx.save();
  ctx.fillStyle = COLORS.text;
  ctx.font = '10px sans-serif';
  ctx.textAlign = 'center';
  const xTicks = xScale.ticks(Math.max(2, Math.floor(chartWidth / 80)));
  const yLabel = height - margins.bottom + 16;
  for (const tick of xTicks) {
    const x = xScale(tick);
    if (x >= margins.left && x <= margins.left + chartWidth) {
      const d = tick as Date;
      const label = `${d.getMonth() + 1}/${d.getDate()}`;
      ctx.fillText(label, x, yLabel);
    }
  }

  // Time axis line
  ctx.beginPath();
  ctx.strokeStyle = COLORS.axisLine;
  ctx.lineWidth = 1;
  ctx.moveTo(margins.left, height - margins.bottom);
  ctx.lineTo(margins.left + chartWidth, height - margins.bottom);
  ctx.stroke();
  ctx.restore();
}
